import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

const BASE_URL = 'https://innovationcoders.com';
const DEFAULT_OG_IMAGE = `${BASE_URL}/og-image.png`;

export interface PageMeta {
  title: string;
  description?: string;
  ogImage?: string;
  ogType?: string; // default 'website'
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
}

function setMetaTag(property: string, content: string, attr: 'name' | 'property' = 'property') {
  let meta = document.querySelector<HTMLMetaElement>(`meta[${attr}="${property}"]`);
  if (meta) {
    meta.setAttribute('content', content);
  } else {
    meta = document.createElement('meta');
    meta.setAttribute(attr, property);
    meta.setAttribute('content', content);
    document.head.appendChild(meta);
  }
}

function removeJsonLd() {
  const existing = document.querySelector('script[data-seo="jsonld"]');
  if (existing) existing.remove();
}

function injectJsonLd(data: Record<string, unknown> | Record<string, unknown>[]) {
  removeJsonLd();
  const script = document.createElement('script');
  script.type = 'application/ld+json';
  script.setAttribute('data-seo', 'jsonld');
  script.textContent = JSON.stringify(Array.isArray(data) ? data : data);
  document.head.appendChild(script);
}

export function usePageMeta({ title, description, ogImage, ogType, jsonLd }: PageMeta) {
  const { pathname } = useLocation();

  useEffect(() => {
    const base = 'Innovation Coders';
    document.title = title === base ? `${base} - Custom Software Development Agency | Calgary` : `${title} | ${base}`;

    const desc = description || 'Calgary-based custom software development agency. Web apps, mobile apps, SaaS, and cloud solutions.';
    // Meta description
    const meta = document.querySelector('meta[name="description"]');
    if (meta) meta.setAttribute('content', desc);

    // Canonical URL
    const canonicalUrl = `${BASE_URL}${pathname === '/' ? '' : pathname}`;
    let link = document.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (link) {
      link.href = canonicalUrl;
    } else {
      link = document.createElement('link');
      link.rel = 'canonical';
      link.href = canonicalUrl;
      document.head.appendChild(link);
    }

    // Open Graph tags
    const fullTitle = title === base ? `${base} - Custom Software Development Agency | Calgary` : `${title} | ${base}`;
    setMetaTag('og:title', fullTitle);
    setMetaTag('og:description', desc);
    setMetaTag('og:url', canonicalUrl);
    setMetaTag('og:image', ogImage || DEFAULT_OG_IMAGE);
    setMetaTag('og:type', ogType || 'website');
    setMetaTag('og:site_name', 'Innovation Coders');

    // Twitter Card tags
    setMetaTag('twitter:card', 'summary_large_image', 'name');
    setMetaTag('twitter:title', fullTitle, 'name');
    setMetaTag('twitter:description', desc, 'name');
    setMetaTag('twitter:image', ogImage || DEFAULT_OG_IMAGE, 'name');

    // JSON-LD structured data
    if (jsonLd) {
      injectJsonLd(jsonLd);
    } else {
      removeJsonLd();
    }

    return () => {
      removeJsonLd();
    };
  }, [title, description, pathname, ogImage, ogType, jsonLd]);
}

/**
 * Generate BreadcrumbList JSON-LD from the current path.
 * Call this in pages that display breadcrumbs.
 */
export function useBreadcrumbJsonLd(items: { name: string; url: string }[]) {
  useEffect(() => {
    const existingBreadcrumb = document.querySelector('script[data-seo="breadcrumb"]');
    if (existingBreadcrumb) existingBreadcrumb.remove();

    if (items.length === 0) return;

    const jsonLd = {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: items.map((item, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        name: item.name,
        item: item.url,
      })),
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.setAttribute('data-seo', 'breadcrumb');
    script.textContent = JSON.stringify(jsonLd);
    document.head.appendChild(script);

    return () => {
      const el = document.querySelector('script[data-seo="breadcrumb"]');
      if (el) el.remove();
    };
  }, [items]);
}
